import React, { useRef, useState } from 'react'
import { AnimatedCheck, Button, OnboardSubtitle, OnboardTitle } from 'components/Antifraude'
import { Alert, AlertTitle, Box, Divider, InputAdornment, TextField } from '@mui/material'
import { AccountCircle, ContactPhone, Email } from '@mui/icons-material/'
import _ from 'lodash'
import { toast } from 'react-toastify'
import Reaptcha from 'reaptcha'
import { formatToCPF, phoneFormat } from 'utils/masks'
import { cpfValid, phoneValidator, ValidateEmail } from 'utils/validators'
import { post, URL_VARIABLES } from 'services/api'

import { Link } from "react-router-dom"
import styles from './styles'
import { ROUTE_CONSTANTS } from "../../../utils/constants/routeConstants"

const Start = () => {
  const classes = styles()

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY

  const captcha = useRef()

  const [formValues, setFormValues] = useState({
    cpf: '',
    phone: '',
    email: '',
  })
  const [formErrors, setFormErrors] = useState({
    cpf: false,
    phone: false,
    email: false,
  })

  const [validCaptcha, setValidCaptcha] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isOnboardingInProgressAlertVisible, setIsOnboardingInProgressAlertVisible] = useState(false)

  const [sent, setSended] = useState(false)

  const validateCaptcha = () => {
    setValidCaptcha(true)
  }

  const expiredCaptcha = () => {
    setValidCaptcha(false)
    captcha.current.reset()
    captcha.current.execute()
    setValidCaptcha(true)
  }

  const onRenderCaptcha = () => {
    captcha.current.execute()
  }

  const validateFields = () => {
    const actions = {
      cpf: cpfValid,
      phone: phoneValidator,
      email: ValidateEmail,
    }
    const errors = _.cloneDeep(formErrors)
    Object.keys(formValues).forEach((key) => {
      errors[key] = !actions[key](formValues[key])
    })
    setFormErrors(errors)
    return {
      isFieldsValid: !Object.keys(errors).some((key) => errors[key]),
      errors: errors,
    }
  }

  const getInvalidFieldNames = (errors) => {
    const names = {
      cpf: 'CPF',
      phone: 'Celular',
      email: 'E-mail',
    }
    const fields = Object.keys(errors).filter((key) => errors[key])

    const fieldNames = fields.map((field) => names[field])

    return fieldNames.join(', ')
  }

  const handleSubmit = async () => {
    const bodyData = _.cloneDeep(formValues)
    bodyData.cpf = bodyData.cpf.replace(/\D/g, '')
    bodyData.phone = bodyData.phone.replace(/\D/g, '')

    setLoading(true)
    setIsOnboardingInProgressAlertVisible(false)

    await post({
      url: '/start',
      data: bodyData,
      token: false,
      source: URL_VARIABLES.ANTIFRAUDE,
    })
      .then((response) => {
        if (response.data?.link) {
          setSended(true)
        }
      })
      .catch((error) => {
        const errorType = error?.response?.data?.error
        if (errorType === 'CPF_BLOCKED' || errorType === 'PROSPECT_ALREADY_APPLIED') {
          toast.error(error.response.data.message)
          setIsOnboardingInProgressAlertVisible(errorType === 'PROSPECT_ALREADY_APPLIED')
        } else {
          toast.error(
            'Houve um erro ao processar a requisição, por favor tente novamente',
          )
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleStartOnboarding = () => {
    const { isFieldsValid, errors } = validateFields()
    if (isFieldsValid) {
      handleSubmit()
    } else {
      toast.error(
        `Verifique os dados digitados nos campos ${getInvalidFieldNames(
          errors,
        )}`,
      )
    }
  }

  const handleChange = (event) => {
    const key = event.target.name
    const currentValue = _.cloneDeep(formValues)
    const { value } = event.target
    switch (key) {
      case 'cpf':
        currentValue.cpf = formatToCPF(value)
        break
      case 'phone':
        currentValue.phone = phoneFormat(value)
        break
      case 'email':
        currentValue.email = value
        break

      default:
        break
    }
    setFormValues(currentValue)
  }

  return (
    <div className={classes.container}>
      {sent ? (
        <div className={classes.sent}>
          <div className={classes.titleBar}>
            <OnboardTitle>Processo de onboarding iniciado</OnboardTitle>
            <OnboardSubtitle>
              Você receberá em instantes um SMS com um link.
            </OnboardSubtitle>
            <OnboardSubtitle>
              Clique nele para realizar a sua jornada antifraude.
            </OnboardSubtitle>
          </div>
          <AnimatedCheck />
        </div>
      ) : (
        <div className={classes.wrapper}>
          <div>
            <div className={classes.titleBar}>
              <OnboardTitle>
                Bem-vindo(a) ao processo de vínculo de assessores de investimento
              </OnboardTitle>
              <OnboardSubtitle>
                Para prosseguirmos, preencha os seguintes dados:
              </OnboardSubtitle>
            </div>
            <Box
              component="form"
              className={classes.formBox}
              noValidate
              autoComplete="on"
            >
              <TextField
                required
                fullWidth
                error={formErrors.cpf}
                name="cpf"
                label="CPF"
                placeholder='123.456.789-00'
                value={formValues.cpf}
                onChange={handleChange}
                variant="standard"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                fullWidth
                error={formErrors.phone}
                name="phone"
                label="Celular"
                placeholder='(11) 91234-5678'
                value={formValues.phone}
                onChange={handleChange}
                variant="standard"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ContactPhone />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                fullWidth
                error={formErrors.email}
                name="email"
                label="E-mail pessoal"
                value={formValues.email}
                placeholder='meu-email-pessoal@gmail.com'
                onChange={handleChange}
                variant="standard"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />

              <Alert severity="info" style={{ marginTop: '8px' }}>
                <AlertTitle>Atenção ao endereço de e-mail!</AlertTitle>
                Entraremos em contato com você em breve; portanto, use seu endereço de e-mail principal. Evite utilizar endereços de e-mail institucionais.
              </Alert>

              <Button
                onClick={handleStartOnboarding}
                disabled={!validCaptcha}
                loading={loading}
              >
                Iniciar Processo
              </Button>

              <Divider variant="middle" className={classes.divider} />

              {isOnboardingInProgressAlertVisible && (
                <Alert severity="error">
                  Ops! Parece que você já possui um processo de vínculo em andamento. Se você já realizou a etapa
                  inicial, clique no link abaixo para prosseguir.
                </Alert>
              )}

              <Link className={classes.link} to={ROUTE_CONSTANTS.HOME}>
                Já realizou o início do processo? <strong>Clique aqui</strong>
              </Link>
            </Box>
          </div>
          <Reaptcha
            ref={captcha}
            sitekey={RECAPTCHA_SITE_KEY}
            onVerify={validateCaptcha}
            onExpire={expiredCaptcha}
            onRender={onRenderCaptcha}
            size="invisible"
            badge="inline"
          />
        </div>
      )}
    </div>
  )
}

export default Start
